import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Upload, X, Plus, Trash2, Edit3,
  Sparkles, PartyPopper,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { profileImageSrc } from "../lib/profileImage";
import { useBranding } from "../lib/branding";
import PragmaSelect from "../components/PragmaSelect";

const ROLE_OPTIONS = [
  "Founder / CEO / Owner",
  "Head of Sales / VP Sales",
  "Sales Manager",
  "Account Executive (AE)",
  "Sales Development Rep (SDR)",
  "Business Development Rep (BDR)",
  "Customer Success Manager",
  "Account Manager",
  "RevOps / Operations",
  "Marketing",
  "Other",
];

const TOTAL_STEPS = 8; // welcome(0), photo(1), company(2), currency(3), deliverables(4), pipeline(5), invite(6), celebration(7)

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const branding = useBranding();
  const [step, setStep] = useState(0);
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  // Guard: users who already finished onboarding shouldn't revisit the wizard.
  useEffect(() => {
    if (user && user.onboarding_completed_at) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const [answers, setAnswers] = useState({
    first_name: "",
    last_name: "",
    photo_url: null,
    company_name: "",
    role_choice: "",
    role_other: "",
    currency: "USD",
    deliverables: [
      { name: "", default_price: "" },
      { name: "", default_price: "" },
      { name: "", default_price: "" },
    ],
    pipeline_stages: [],
    invite: { first_name: "", last_name: "", email: "", role: "sales_rep" },
  });

  // Load bootstrap state
  useEffect(() => {
    let alive = true;
    api.get("/onboarding/state").then(({ data }) => {
      if (!alive) return;
      setState(data);
      setAnswers((a) => ({
        ...a,
        first_name: data.user?.first_name || "",
        last_name: data.user?.last_name || "",
        photo_url: data.user?.profile_image_url || null,
        company_name: data.tenant?.name || "",
        currency: data.tenant?.currency || "USD",
        pipeline_stages: (data.default_pipeline?.stages || []).map((s) => ({ ...s })),
      }));
    }).catch(() => { toast.error("Could not load onboarding data"); });
    return () => { alive = false; };
  }, []);

  const complete = async () => {
    try {
      await api.post("/auth/me/complete-onboarding");
      if (refresh) await refresh();
    } catch { /* non-fatal — user can retry via layout */ }
    navigate("/", { replace: true });
  };

  const goNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  // Per-step save helpers ------------------------------------------------------
  const saveCompanyRole = async () => {
    setSaving(true);
    try {
      const finalRole = answers.role_choice === "Other" ? answers.role_other.trim() : answers.role_choice;
      const promises = [];
      if (answers.company_name.trim() && answers.company_name.trim() !== state.tenant?.name) {
        promises.push(api.put("/settings", { company_name: answers.company_name.trim() }));
      }
      if (finalRole) {
        promises.push(api.patch("/auth/me/profile", { job_title: finalRole }));
      }
      await Promise.all(promises);
      if (refresh) await refresh();
      goNext();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const saveCurrency = async () => {
    setSaving(true);
    try {
      if (answers.currency && answers.currency !== state.tenant?.currency) {
        await api.put("/settings", { currency: answers.currency });
      }
      goNext();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const saveDeliverables = async () => {
    const filled = answers.deliverables.filter((d) => d.name.trim());
    if (filled.length === 0) {
      toast.error("Add at least one product or service to continue");
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        filled.map((d) =>
          api.post("/deliverables", {
            name: d.name.trim(),
            default_price: parseFloat(d.default_price) || 0,
            active: true,
          })
        )
      );
      goNext();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const savePipeline = async () => {
    if (!state?.default_pipeline?.id) { goNext(); return; }
    setSaving(true);
    try {
      await api.patch(`/pipelines/${state.default_pipeline.id}`, {
        name: state.default_pipeline.name,
        stages: answers.pipeline_stages.map((s, i) => ({
          ...s, order: i, probability: parseFloat(s.probability) || 0,
        })),
      });
      goNext();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const sendInvite = async () => {
    const { first_name, email, role } = answers.invite;
    if (!first_name.trim() || !email.trim()) {
      toast.error("First name and email are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", {
        first_name: first_name.trim(),
        last_name: answers.invite.last_name.trim(),
        email: email.trim(),
        role,
      });
      toast.success(`Invite sent to ${email.trim()}`);
      goNext();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send invite"); }
    setSaving(false);
  };

  if (!state) {
    return (
      <div style={splashStyle}>
        <div className="mono text-mute text-sm">loading your onboarding…</div>
      </div>
    );
  }

  return (
    <div style={splashStyle} data-testid="onboarding-wizard">
      {/* Top bar: logo only — no skip (setup is required) */}
      <div style={topBarStyle}>
        <div className="flex items-center gap-2">
          <img
            src={branding?.logo_data_url || "/pragma-login-logo-v4.png"}
            alt="Pragma"
            style={{ height: 32 }}
            data-testid="onboarding-logo"
          />
        </div>
      </div>

      {/* Progress dots */}
      {step > 0 && step < TOTAL_STEPS - 1 && (
        <div style={progressBarStyle}>
          {Array.from({ length: TOTAL_STEPS - 2 }).map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: i < step ? "var(--pg-accent, #5B4FE8)" : "var(--pg-border)",
              transition: "background 0.3s ease",
            }} />
          ))}
        </div>
      )}

      {/* Main card */}
      <div style={cardWrapStyle}>
        <div style={cardStyle}>
          {step === 0 && <StepWelcome onNext={goNext} firstName={answers.first_name} />}
          {step === 1 && <StepPhoto answers={answers} setAnswers={setAnswers} onNext={goNext} refresh={refresh} />}
          {step === 2 && <StepCompanyRole answers={answers} setAnswers={setAnswers} onNext={saveCompanyRole} saving={saving} />}
          {step === 3 && <StepCurrency answers={answers} setAnswers={setAnswers} currencies={state.currencies} onNext={saveCurrency} saving={saving} />}
          {step === 4 && <StepDeliverables answers={answers} setAnswers={setAnswers} onNext={saveDeliverables} saving={saving} />}
          {step === 5 && <StepPipeline answers={answers} setAnswers={setAnswers} defaultPipeline={state.default_pipeline} onNext={savePipeline} saving={saving} />}
          {step === 6 && <StepInvite answers={answers} setAnswers={setAnswers} onSend={sendInvite} onSkip={goNext} saving={saving} />}
          {step === 7 && <StepCelebration onFinish={complete} />}
        </div>

        {/* Back link (below the card, only for real question steps) */}
        {step > 0 && step < TOTAL_STEPS - 1 && (
          <button onClick={goBack} style={backLinkStyle} className="mono text-xs" data-testid="onboarding-back">
            <ArrowLeft size={12} /> Back
          </button>
        )}
      </div>
    </div>
  );
}

// ─── STEP: Welcome ────────────────────────────────────────────────────────────
function StepWelcome({ onNext, firstName }) {
  return (
    <div className="text-center">
      <div style={heroIconStyle}><Sparkles size={40} /></div>
      <h1 style={h1Style}>
        Welcome to Pragma{firstName ? `, ${firstName}` : ""}.
      </h1>
      <p style={leadStyle}>
        Let's spend two minutes setting up your workspace so it fits the way you sell.
        You can change any of this later in Settings.
      </p>
      <PrimaryButton onClick={onNext} testId="onboarding-welcome-next">
        Let's go <ArrowRight size={16} />
      </PrimaryButton>
    </div>
  );
}

// ─── STEP: Photo ──────────────────────────────────────────────────────────────
function StepPhoto({ answers, setAnswers, onNext, refresh }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const initials = ((answers.first_name?.[0] || "") + (answers.last_name?.[0] || "")).toUpperCase() || "?";

  const pick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Only image files are accepted"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Image too large — max 2MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload/profile-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAnswers({ ...answers, photo_url: data.profile_image_url });
      if (refresh) await refresh();
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    setUploading(false);
  };

  return (
    <StepShell
      eyebrow="1 · Profile photo"
      title="Put a face to your name"
      subtitle="Your photo shows up on activity, deals and emails so teammates always know who did what."
    >
      <div className="flex flex-col items-center gap-4">
        <div style={photoStyle}>
          {answers.photo_url
            ? <img src={profileImageSrc(answers.photo_url)} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <span style={{ fontSize: 40, color: "var(--pg-text-mute)" }}>{initials}</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={pick} disabled={uploading} className="pg-btn" data-testid="onboarding-photo-upload">
            <Upload size={14} /> {uploading ? "Uploading…" : (answers.photo_url ? "Change photo" : "Upload photo")}
          </button>
          {answers.photo_url && (
            <button
              onClick={async () => {
                try {
                  await api.patch("/auth/me/profile", { profile_image_url: "" });
                  setAnswers({ ...answers, photo_url: null });
                  if (refresh) await refresh();
                  toast.success("Photo removed");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to remove");
                }
              }}
              className="pg-btn"
              data-testid="onboarding-photo-remove"
            >
              <X size={14} /> Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} data-testid="onboarding-photo-input" />
      </div>
      <PrimaryButton onClick={onNext} testId="onboarding-photo-next">
        Continue <ArrowRight size={16} />
      </PrimaryButton>
      <div style={centeredSkipWrap}>
        <button onClick={onNext} className="mono text-xs uppercase tracking-wider" style={skipLinkStyle} data-testid="onboarding-photo-skip">
          Skip for now
        </button>
      </div>
    </StepShell>
  );
}

// ─── STEP: Company + Role ─────────────────────────────────────────────────────
function StepCompanyRole({ answers, setAnswers, onNext, saving }) {
  const showOther = answers.role_choice === "Other";
  const disabled = !answers.company_name.trim() || !answers.role_choice || (showOther && !answers.role_other.trim()) || saving;
  return (
    <StepShell
      eyebrow="2 · Your company"
      title="Where do you sell?"
      subtitle="Tell us a little about your company and what you do there."
    >
      <div className="space-y-4">
        <div>
          <label style={labelStyle}>Company name</label>
          <input
            className="pg-input" autoFocus
            value={answers.company_name}
            onChange={(e) => setAnswers({ ...answers, company_name: e.target.value })}
            placeholder="Acme Inc."
            data-testid="onboarding-company-name"
          />
        </div>
        <div>
          <label style={labelStyle}>Your role at {answers.company_name || "the company"}</label>
          <PragmaSelect
            value={answers.role_choice}
            onChange={(v) => setAnswers({ ...answers, role_choice: v })}
            options={ROLE_OPTIONS.map((r) => ({ value: r, label: r }))}
            placeholder="Choose a role…"
            testId="onboarding-role-select"
          />
        </div>
        {showOther && (
          <div>
            <label style={labelStyle}>Tell us your role</label>
            <input
              className="pg-input"
              value={answers.role_other}
              onChange={(e) => setAnswers({ ...answers, role_other: e.target.value })}
              placeholder="e.g. Chief of Staff"
              data-testid="onboarding-role-other"
            />
          </div>
        )}
      </div>
      <PrimaryButton onClick={onNext} disabled={disabled} testId="onboarding-company-next">
        {saving ? "Saving…" : <>Continue <ArrowRight size={16} /></>}
      </PrimaryButton>
    </StepShell>
  );
}

// ─── STEP: Currency ───────────────────────────────────────────────────────────
function StepCurrency({ answers, setAnswers, currencies, onNext, saving }) {
  return (
    <StepShell
      eyebrow="3 · Currency"
      title="What currency do you operate in?"
      subtitle="Every deal value and forecast in Pragma will be shown in this currency."
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
        {(currencies || []).map((c) => {
          const active = answers.currency === c.code;
          return (
            <button
              key={c.code}
              onClick={() => setAnswers({ ...answers, currency: c.code })}
              data-testid={`onboarding-currency-${c.code}`}
              style={{
                padding: "12px 10px",
                border: `1px solid ${active ? "var(--pg-accent, #5B4FE8)" : "var(--pg-border)"}`,
                background: active ? "rgba(91,79,232,0.08)" : "var(--pg-surface-2)",
                borderRadius: 8,
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <div className="mono text-xs" style={{ color: "var(--pg-text)" }}>{c.code}</div>
              <div className="text-xs text-mute" style={{ marginTop: 2 }}>{c.symbol}{c.label ? ` · ${c.label}` : ""}</div>
            </button>
          );
        })}
      </div>
      <PrimaryButton onClick={onNext} disabled={saving} testId="onboarding-currency-next">
        {saving ? "Saving…" : <>Continue <ArrowRight size={16} /></>}
      </PrimaryButton>
    </StepShell>
  );
}

// ─── STEP: Deliverables ───────────────────────────────────────────────────────
function StepDeliverables({ answers, setAnswers, onNext, saving }) {
  const setRow = (i, key, val) => {
    const arr = answers.deliverables.map((d, idx) => idx === i ? { ...d, [key]: val } : d);
    setAnswers({ ...answers, deliverables: arr });
  };
  const filled = answers.deliverables.filter((d) => d.name.trim()).length;
  const disabled = filled === 0 || saving;
  return (
    <StepShell
      eyebrow="4 · Products & services"
      title="What do you sell?"
      subtitle="Add up to three of your core products or services. You can attach these to any deal and use them to auto-calculate deal value."
    >
      <div className="space-y-3">
        {answers.deliverables.map((d, i) => (
          <div key={i} className="flex gap-2 items-start" data-testid={`onboarding-deliverable-row-${i}`}>
            <div className="flex-1">
              <input
                className="pg-input"
                placeholder={i === 0 ? "e.g. Website Design (required)" : "e.g. SEO Retainer (optional)"}
                value={d.name}
                onChange={(e) => setRow(i, "name", e.target.value)}
                data-testid={`onboarding-deliverable-name-${i}`}
              />
            </div>
            <div style={{ width: 140 }}>
              <input
                className="pg-input" type="number" min="0" step="0.01"
                placeholder="Price"
                value={d.default_price}
                onChange={(e) => setRow(i, "default_price", e.target.value)}
                data-testid={`onboarding-deliverable-price-${i}`}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mono text-[10px] text-mute mt-3">
        {filled === 0 ? "Add at least one to continue." : `${filled} ready · you can continue with just one.`}
      </div>
      <PrimaryButton onClick={onNext} disabled={disabled} testId="onboarding-deliverables-next">
        {saving ? "Saving…" : <>Continue <ArrowRight size={16} /></>}
      </PrimaryButton>
    </StepShell>
  );
}

// ─── STEP: Pipeline ───────────────────────────────────────────────────────────
function StepPipeline({ answers, setAnswers, defaultPipeline, onNext, saving }) {
  const [editing, setEditing] = useState(false);
  const stages = answers.pipeline_stages;

  const setStage = (i, key, val) => {
    const arr = stages.map((s, idx) => idx === i ? { ...s, [key]: val } : s);
    setAnswers({ ...answers, pipeline_stages: arr });
  };
  const addStage = () => {
    setAnswers({ ...answers, pipeline_stages: [...stages, { name: "New Stage", probability: 50 }] });
  };
  const removeStage = (i) => {
    setAnswers({ ...answers, pipeline_stages: stages.filter((_, idx) => idx !== i) });
  };

  return (
    <StepShell
      eyebrow="5 · Acquisition pipeline"
      title="How do new deals flow?"
      subtitle={`This is your Acquisition pipeline — how a new customer moves from qualified to won. Continue with our defaults or edit the stages now.`}
    >
      {!editing ? (
        <div className="space-y-2" data-testid="onboarding-pipeline-preview">
          {stages.map((s, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2" style={stageRowStyle}>
              <div className="flex items-center gap-3">
                <div className="mono text-[10px] text-mute" style={{ width: 20 }}>{String(i + 1).padStart(2, "0")}</div>
                <div style={{ color: "var(--pg-text)" }}>{s.name}</div>
              </div>
              <div className="mono text-[11px] text-mute">{s.probability}% win prob.</div>
            </div>
          ))}
          <div className="flex items-center justify-between mt-6 gap-3">
            <button onClick={() => setEditing(true)} className="pg-btn" data-testid="onboarding-pipeline-edit">
              <Edit3 size={14} /> Edit
            </button>
            <PrimaryButton onClick={onNext} disabled={saving} testId="onboarding-pipeline-continue-default">
              {saving ? "Saving…" : <>Continue with defaults <ArrowRight size={16} /></>}
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="onboarding-pipeline-editor">
          {stages.map((s, i) => (
            <div key={i} className="flex items-center gap-2 p-2" style={stageRowStyle}>
              <input
                className="pg-input flex-1"
                value={s.name}
                onChange={(e) => setStage(i, "name", e.target.value)}
                data-testid={`onboarding-pipeline-stage-name-${i}`}
              />
              <input
                className="pg-input" style={{ width: 80 }} type="number" min="0" max="100"
                value={s.probability}
                onChange={(e) => setStage(i, "probability", e.target.value)}
                data-testid={`onboarding-pipeline-stage-prob-${i}`}
              />
              <span className="text-mute mono text-xs">%</span>
              <button onClick={() => removeStage(i)} className="pg-btn pg-btn-danger" aria-label="remove"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={addStage} className="pg-btn mt-2" data-testid="onboarding-pipeline-add-stage">
            <Plus size={14} /> Add stage
          </button>
          <div className="flex items-center justify-between mt-6 gap-3">
            <button onClick={() => setEditing(false)} className="mono text-xs uppercase tracking-wider" style={skipLinkStyle}>
              Cancel edit
            </button>
            <PrimaryButton onClick={onNext} disabled={saving} testId="onboarding-pipeline-save">
              {saving ? "Saving…" : <>Save & continue <ArrowRight size={16} /></>}
            </PrimaryButton>
          </div>
        </div>
      )}
    </StepShell>
  );
}

// ─── STEP: Invite ─────────────────────────────────────────────────────────────
function StepInvite({ answers, setAnswers, onSend, onSkip, saving }) {
  const inv = answers.invite;
  const setInv = (patch) => setAnswers({ ...answers, invite: { ...inv, ...patch } });
  const canSend = inv.first_name.trim() && inv.email.trim() && !saving;
  return (
    <StepShell
      eyebrow="6 · Team"
      title="Don't leave your team behind"
      subtitle="Invite your first teammate now — they'll get an email with a one-click link to set their own password."
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label style={labelStyle}>First name</label>
          <input className="pg-input" value={inv.first_name}
            onChange={(e) => setInv({ first_name: e.target.value })}
            data-testid="onboarding-invite-first-name" />
        </div>
        <div>
          <label style={labelStyle}>Last name</label>
          <input className="pg-input" value={inv.last_name}
            onChange={(e) => setInv({ last_name: e.target.value })}
            data-testid="onboarding-invite-last-name" />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Email</label>
        <input className="pg-input" type="email" value={inv.email}
          onChange={(e) => setInv({ email: e.target.value })}
          placeholder="teammate@company.com"
          data-testid="onboarding-invite-email" />
      </div>
      <div>
        <label style={labelStyle}>Role</label>
        <PragmaSelect
          value={inv.role}
          onChange={(v) => setInv({ role: v })}
          options={[
            { value: "sales_rep", label: "Sales Rep" },
            { value: "manager",   label: "Manager" },
            { value: "admin",     label: "Admin" },
          ]}
          testId="onboarding-invite-role"
        />
      </div>
      <PrimaryButton onClick={onSend} disabled={!canSend} testId="onboarding-invite-send">
        {saving ? "Sending…" : <>Send invite <ArrowRight size={16} /></>}
      </PrimaryButton>
      <div style={centeredSkipWrap}>
        <button onClick={onSkip} className="mono text-xs uppercase tracking-wider" style={skipLinkStyle} data-testid="onboarding-invite-skip">
          Skip for now
        </button>
      </div>
    </StepShell>
  );
}

// ─── STEP: Celebration ────────────────────────────────────────────────────────
function StepCelebration({ onFinish }) {
  return (
    <div className="text-center" style={{ position: "relative" }}>
      <ConfettiBurst />
      <div style={{ ...heroIconStyle, background: "rgba(91,79,232,0.12)", position: "relative", zIndex: 2 }}>
        <PartyPopper size={40} />
      </div>
      <h1 style={{ ...h1Style, position: "relative", zIndex: 2 }}>You're all set!</h1>
      <p style={{ ...leadStyle, position: "relative", zIndex: 2 }}>
        Let's get your sales moving. Your Pragma workspace is ready — jump into your dashboard and start adding leads, deals, and moves.
      </p>
      <div style={{ position: "relative", zIndex: 2 }}>
        <PrimaryButton onClick={onFinish} testId="onboarding-finish">
          Open my dashboard <ArrowRight size={16} />
        </PrimaryButton>
      </div>
    </div>
  );
}

// Confetti — 40 small squares of accent-family colors, each with its own random
// initial angle, distance and rotation. Pure CSS animation, no runtime canvas.
function ConfettiBurst() {
  const pieces = useMemo(() => {
    const colors = ["#5B4FE8", "#22C55E", "#F59E0B", "#EC4899", "#38BDF8", "#F472B6", "#A78BFA"];
    return Array.from({ length: 42 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 42 + (Math.random() - 0.5) * 0.3;
      const distance = 90 + Math.random() * 140;
      return {
        id: i,
        color: colors[i % colors.length],
        dx: Math.cos(angle) * distance,
        dy: Math.sin(angle) * distance - 20, // bias slightly upward
        rot: Math.random() * 720 - 360,
        size: 6 + Math.random() * 6,
        delay: Math.random() * 120,
        shape: i % 3, // 0 sq, 1 rect, 2 rounded
      };
    });
  }, []);
  return (
    <div
      aria-hidden
      data-testid="onboarding-celebration-burst"
      style={{
        position: "absolute", inset: 0,
        pointerEvents: "none",
        overflow: "visible",
        zIndex: 1,
      }}
    >
      {pieces.map((p) => (
        <span
          key={p.id}
          style={{
            position: "absolute",
            top: "50%", left: "50%",
            width: p.shape === 1 ? p.size * 1.6 : p.size,
            height: p.shape === 1 ? p.size * 0.55 : p.size,
            background: p.color,
            borderRadius: p.shape === 2 ? "50%" : 1,
            opacity: 0,
            transform: "translate(-50%, -50%)",
            animation: `pragmaConfetti 1500ms cubic-bezier(0.16, 1, 0.3, 1) ${p.delay}ms forwards`,
            "--dx": `${p.dx}px`,
            "--dy": `${p.dy}px`,
            "--rot": `${p.rot}deg`,
          }}
        />
      ))}
    </div>
  );
}

// ─── Shared shells & styles ───────────────────────────────────────────────────
function StepShell({ eyebrow, title, subtitle, children }) {
  return (
    <>
      <div className="mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--pg-accent, #5B4FE8)", marginBottom: 10 }}>
        {eyebrow}
      </div>
      <h1 style={h1Style}>{title}</h1>
      {subtitle && <p style={leadStyle}>{subtitle}</p>}
      <div style={{ marginTop: 24 }}>{children}</div>
    </>
  );
}

function PrimaryButton({ children, onClick, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        marginTop: 28,
        width: "100%",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        padding: "14px 20px",
        background: disabled ? "var(--pg-surface-2)" : "var(--pg-accent, #5B4FE8)",
        color: disabled ? "var(--pg-text-mute)" : "#fff",
        border: 0,
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: 0.2,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s ease",
      }}
    >
      {children}
    </button>
  );
}

const splashStyle = {
  position: "fixed", inset: 0,
  background: "var(--pg-bg, #FAFAF7)",
  zIndex: 200,
  overflow: "auto",
  display: "flex", flexDirection: "column",
  fontFamily: "'Google Sans', 'Inter', system-ui, sans-serif",
};

const topBarStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "22px 40px",
  borderBottom: "1px solid var(--pg-border)",
  background: "var(--pg-surface, #fff)",
};

const progressBarStyle = {
  display: "flex", gap: 6, padding: "18px 40px 0",
  maxWidth: 560, width: "100%", margin: "0 auto",
};

const cardWrapStyle = {
  flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
  padding: "40px 20px 60px",
};

const cardStyle = {
  width: "100%", maxWidth: 560,
  background: "var(--pg-surface, #fff)",
  border: "1px solid var(--pg-border)",
  borderRadius: 16,
  padding: "40px 40px 36px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.02), 0 4px 20px rgba(0,0,0,0.04)",
};

const h1Style = {
  fontSize: 30, lineHeight: 1.15, letterSpacing: "-0.5px",
  color: "var(--pg-text)", margin: "0 0 10px",
  fontWeight: 600,
};

const leadStyle = {
  fontSize: 15, lineHeight: 1.55,
  color: "var(--pg-text-mute)",
  margin: "0 0 0",
};

const labelStyle = {
  display: "block",
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em",
  color: "var(--pg-text-mute)", marginBottom: 6,
};

const skipLinkStyle = {
  background: "none", border: 0, padding: 0,
  color: "var(--pg-text-mute)", cursor: "pointer",
  letterSpacing: "0.12em",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const centeredSkipWrap = {
  display: "flex", justifyContent: "center",
  marginTop: 14,
};

const backLinkStyle = {
  marginTop: 20, background: "none", border: 0, padding: 0,
  color: "var(--pg-text-mute)", cursor: "pointer",
  display: "flex", alignItems: "center", gap: 6,
};

const heroIconStyle = {
  width: 80, height: 80, borderRadius: "50%",
  background: "rgba(91,79,232,0.10)",
  color: "var(--pg-accent, #5B4FE8)",
  display: "flex", alignItems: "center", justifyContent: "center",
  margin: "0 auto 24px",
};

const photoStyle = {
  width: 120, height: 120, borderRadius: "50%",
  background: "var(--pg-surface-2)",
  border: "1px solid var(--pg-border)",
  display: "flex", alignItems: "center", justifyContent: "center",
  overflow: "hidden",
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
};

const stageRowStyle = {
  background: "var(--pg-surface-2)",
  border: "1px solid var(--pg-border)",
  borderRadius: 8,
};
